// The monster layer: the twenty bodies of round 109 on the map, animated
// (R2719, v2-the-deep.md §8.4, ui-map-interface.md §6.1.15).
//
// **What it draws.** Each hull of the NPC side whose class is one of the
// five monster kinds (`MONSTER_CLASSES`) draws here, as the body of its
// design (`FleetState.monster_design`, 0 to 3, the design of its lair),
// at the place and the bow the fleet layer draws it at. The fleet layer
// takes the ragged ring of that hull away (`drawn`, through
// `setHullSuppressed`) and keeps its bars, its order line and its wake, so
// the monster stays a fleet of the map in every other way.
//
// **The act** (`npc_act`, `npc_act_left_ms`) drives the body: IDLE holds,
// MOVE swims, TELL warns (the dash of the Murkhide, the hum of the
// Thrummer, the swell of the Quillback), STRIKE bites, HOLD grips, LAUNCH
// spends the pods and grows them back, PULSE discharges, GATHER wraps a
// hull, SCATTER breaks up (`actPose` of `render/monster-bodies.ts`). A
// timed act reads its share from the figure on the wire, counted down on
// this client between two frames in map seconds (the digest rule of
// R2594). A server that states no act gets the act from the motion: a
// body that moves faster than `MONSTER_LOOK.movePxPerSec` swims. A gun hit
// whose shot comes from a monster plays its strike for
// `MONSTER_LOOK.strikeSec` when the wire states no act, and the combat
// layer draws no gun salvo for it (`combat.ts`, R2719).
//
// **The zoom.** Under `MONSTER_LOOK.bodyMinZoom` (the camera zoom times
// the mark scale) a monster draws the still mark of its kind
// (`render/monster-marks.ts`), whose moving parts would blur at that size.
//
// **The gate.** The layer draws nothing while the world has not latched
// `pirates` (`setGate`), and it keeps no record then, so a world without
// the flag renders as before.
//
// **The cost.** A body is 40 to 200 capsules and circles a frame; a map
// holds at most ten monsters (two lairs for each 50 seats, R2954), and the
// layer culls each one on its own reach. The quill pod's burst draws here
// too (`podBurst`), from a pool of eight.

import { NpcAct, NpcKind, type FleetState } from '../../gen/proto/aquaphobia';
import { FLEET_NPC_MARK_PX, MONSTER_LOOK } from '../../gen/presentation';
import {
  ACT, MONSTER_BODIES, MONSTER_EXTENT, POD_BURST_SEC, actPose, drawPodBurst, newMonsterState,
  type MonsterState,
} from '../../render/monster-bodies';
import { drawKindMark } from '../../render/monster-marks';
import { monsterKindOf } from '../../render/monster-kinds';
import type { SignaturePose } from '../../render/signature-looks';
import { MPX_PER_PX } from '../../world';
import { outside, type DrawCtx, type FrameInfo, type Layer } from '../layer';
import type { BatchSource, CircleSink, QuadSink, SegmentSink } from './batches';
import { BatchPen, GLOW_KEY } from './monster-pen';
import { zoomOf } from './shared';

/** Where the layer reads the world. */
export interface MonsterFeeds {
  /** One hull as the fleet layer draws it, into `out`; false when the fleet
   *  layer holds no such hull. */
  pose(id: number, out: SignaturePose): boolean;
  /** The nearest visible hull of a faction inside `r`, or null. */
  foe(x: number, y: number, r: number): { x: number; y: number } | null;
  /** Every visible hull of a faction inside `r`, as x, y pairs into `out`;
   *  the count. Without it the discharge arcs to the nearest hull alone. */
  foes?(x: number, y: number, r: number, out: Float32Array): number;
  /** Map seconds for one wall second: the tactical scale. */
  mapSecPerWallSec(): number;
}

/** What the card reads of a monster. */
export interface MonsterRead {
  kind: number;
  design: number;
  act: NpcAct;
}

/** One monster the layer knows. */
interface Rec {
  id: number;
  kind: number;
  design: number;
  act: NpcAct;
  /** Layer seconds when the act began on this client. */
  actAt: number;
  /** The map ms left of a timed act on its first frame, and on the last. */
  full: number;
  left: number;
  leftAt: number;
  /** The place on the last State frame, world px: the shot test. */
  wireX: number;
  wireY: number;
  hullPct: number;
  /** A phase of the body's own clock, so two monsters do not swim in step. */
  offset: number;
  /** The drawn place of the last frame, and the low-passed speed. */
  lastX: number;
  lastY: number;
  speed: number;
  /** A gun hit of this monster: when, and the victim's point. */
  strikeAt: number;
  strikeX: number;
  strikeY: number;
}

/** The act a gun hit plays, by kind, while the wire states none. */
const STRIKE_ACT: readonly number[] = [ACT.strike, ACT.strike, ACT.launch, ACT.pulse, ACT.gather];

/** The acts that look at a target. */
function aims(act: number): boolean {
  return act === ACT.tell || act === ACT.strike || act === ACT.hold || act === ACT.pulse || act === ACT.gather;
}

/** How far a body looks for its target, in local units: the Thrummer's
 *  discharge reach and a little more. */
const AIM_UNITS = 90;

/** A pod burst in the water. */
interface Burst { x: number; y: number; design: number; age: number }
const BURSTS = 8;

const NO_POSE = (): SignaturePose => ({
  x: 0, y: 0, head: 0, faction: 0, cls: 0, own: false, alpha: 1, sonarPx: 0, tx: 0, ty: 0, k: 1, cell: '',
});

export class MonsterLayer implements Layer {
  readonly name = 'monsters';

  private readonly quads: QuadSink;
  private readonly segs: SegmentSink;
  private readonly circles: CircleSink;
  private readonly pen: BatchPen;
  private shown = true;
  private on = false;
  private time = 0;
  private readonly recs = new Map<number, Rec>();
  /** The monsters this layer draws: the fleet layer takes their mark away. */
  readonly drawn = new Set<number>();
  private readonly pose: SignaturePose = NO_POSE();
  private readonly st: MonsterState = newMonsterState();
  private readonly bursts: Burst[] = [];
  private nBursts = 0;

  constructor(src: BatchSource, private readonly feeds: MonsterFeeds) {
    this.quads = src.newQuads(64);
    this.segs = src.newSegments(2048);
    this.circles = src.newCircles(1024);
    this.pen = new BatchPen(this.segs, this.circles, this.quads, src.sprite(GLOW_KEY));
    for (let i = 0; i < BURSTS; i++) this.bursts.push({ x: 0, y: 0, design: 0, age: 0 });
  }

  /** Whether the world latched `pirates`. Off drops every record. */
  setGate(on: boolean): void {
    if (on === this.on) return;
    this.on = on;
    if (!on) this.clear();
  }

  get gate(): boolean { return this.on; }

  /** Drops every monster and every burst (a new hello, a seek). */
  clear(): void {
    this.recs.clear();
    this.drawn.clear();
    this.nBursts = 0;
  }

  setVisible(v: boolean): void { this.shown = v; }
  get visible(): boolean { return this.shown; }

  /** How many monsters the layer knows. */
  get count(): number { return this.recs.size; }

  /** The fleets of one State frame, as `SignatureLayer.applyState` takes them. */
  applyState(fleets: readonly FleetState[], removed: readonly number[], full: boolean): void {
    if (!this.on) return;
    if (full) {
      const ids = new Set<number>();
      for (const w of fleets) ids.add(w.id);
      for (const id of [...this.recs.keys()]) if (!ids.has(id)) this.forget(id);
    }
    for (const id of removed) this.forget(id);
    for (const w of fleets) this.observe(w);
  }

  private forget(id: number): void {
    this.recs.delete(id);
    this.drawn.delete(id);
  }

  private observe(w: FleetState): void {
    const kind = monsterKindOf(w.cls);
    if (w.npc !== NpcKind.NPC_KIND_MONSTER || kind < 0) {
      if (this.recs.has(w.id)) this.forget(w.id);
      return;
    }
    const t = this.time;
    let r = this.recs.get(w.id);
    if (r === undefined) {
      r = {
        id: w.id, kind, design: 0, act: w.npcAct, actAt: t, full: w.npcActLeftMs, left: w.npcActLeftMs, leftAt: t,
        wireX: 0, wireY: 0, hullPct: 100, offset: (w.id * 0.618) % 1 * 10,
        lastX: NaN, lastY: NaN, speed: 0, strikeAt: -Infinity, strikeX: 0, strikeY: 0,
      };
      this.recs.set(w.id, r);
      this.drawn.add(w.id);
    }
    r.kind = kind;
    r.design = Math.max(0, Math.min(MONSTER_BODIES[kind].length - 1, w.monsterDesign));
    if (w.npcAct !== r.act) {
      r.act = w.npcAct;
      r.actAt = t;
      r.full = w.npcActLeftMs;
    } else if (w.npcActLeftMs > r.full) {
      r.full = w.npcActLeftMs;
    }
    r.left = w.npcActLeftMs;
    r.leftAt = t;
    r.wireX = w.xMpx / MPX_PER_PX;
    r.wireY = w.yMpx / MPX_PER_PX;
    if (w.hullPct > 0) r.hullPct = w.hullPct;
  }

  /** The monster whose last wire place lies inside the mark box of a point,
   *  or null: the shooter of a gun hit. */
  private shooterAt(x: number, y: number): Rec | null {
    let best: Rec | null = null;
    let bestD = FLEET_NPC_MARK_PX;
    for (const r of this.recs.values()) {
      const d = Math.hypot(r.wireX - x, r.wireY - y);
      if (d <= bestD) { bestD = d; best = r; }
    }
    return best;
  }

  /** Whether a shot from this point is a monster's (`combat.ts` asks it). */
  firedBy(x: number, y: number): boolean {
    return this.on && this.shooterAt(x, y) !== null;
  }

  /**
   * A gun hit from the point `(sx, sy)` on the point `(vx, vy)`. When the
   * shooter is a monster, its body plays its strike on the victim. Answers
   * whether it was a monster.
   */
  struck(sx: number, sy: number, vx: number, vy: number): boolean {
    if (!this.on) return false;
    const r = this.shooterAt(sx, sy);
    if (!r) return false;
    if (this.time - r.strikeAt > MONSTER_LOOK.strikeSec) r.strikeAt = this.time;
    r.strikeX = vx; r.strikeY = vy;
    return true;
  }

  /** The pod design of the Quillback nearest to a point, 0 when the layer
   *  knows none: the round layer draws a quill pod with it. */
  podDesignAt(x: number, y: number): number {
    let best = 0, bestD = Infinity;
    for (const r of this.recs.values()) {
      if (r.kind !== 2) continue;
      const d = Math.hypot(r.wireX - x, r.wireY - y);
      if (d < bestD) { bestD = d; best = r.design; }
    }
    return best;
  }

  /** A quill pod went off at a point: its burst in the NPC ink. A full pool
   *  gives the slot of the oldest burst, because the swap-remove of a spent
   *  burst leaves the pool in no order of age (R3014). */
  podBurst(x: number, y: number, design: number): void {
    if (!this.on) return;
    let b: Burst;
    if (this.nBursts < BURSTS) b = this.bursts[this.nBursts++];
    else {
      b = this.bursts[0];
      for (let i = 1; i < BURSTS; i++) if (this.bursts[i].age > b.age) b = this.bursts[i];
    }
    b.x = x; b.y = y; b.design = design; b.age = 0;
  }

  /** What the card states of a monster, or null. */
  read(id: number): MonsterRead | null {
    const r = this.recs.get(id);
    return r ? { kind: r.kind, design: r.design, act: r.act } : null;
  }

  update(frame: FrameInfo): void {
    const dt = Math.min(frame.dtMs, 100) / 1000;
    this.time += dt;
    this.quads.begin();
    this.segs.begin();
    this.circles.begin();
    if (!this.on) return;
    const zoom = zoomOf(frame.cam.zoom);
    const ew = Math.pow(zoom, -0.45);
    for (const r of this.recs.values()) this.packOne(r, frame, zoom, ew, dt);
    this.packBursts(frame, dt);
  }

  private packOne(r: Rec, frame: FrameInfo, zoom: number, ew: number, dt: number): void {
    const p = this.pose;
    if (!this.feeds.pose(r.id, p)) return;
    // The speed off the drawn place, low-passed, for a wire with no act.
    if (!Number.isNaN(r.lastX) && dt > 0) {
      const inst = Math.hypot(p.x - r.lastX, p.y - r.lastY) / dt;
      r.speed += (inst - r.speed) * Math.min(1, 4 * dt);
    }
    r.lastX = p.x; r.lastY = p.y;
    if (p.alpha < 0.01) return;
    const k = p.k > 0 ? p.k : 1;
    const pad = MONSTER_EXTENT[r.kind] * k;
    const c = frame.cull;
    if (outside(p.x, p.y, c.x0 - pad, c.y0 - pad, c.x1 + pad, c.y1 + pad)) return;
    const pen = this.pen;
    pen.place(p.x, p.y, p.head, k);
    pen.ew = ew / k;
    pen.alpha = p.alpha;
    if (zoom * k < MONSTER_LOOK.bodyMinZoom) {
      drawKindMark(r.kind, pen);
      return;
    }
    // The act: the wire's, else the motion's; a gun hit plays the strike.
    let act: number = r.act;
    let phase = this.time - r.actAt;
    let share = -1;
    if (r.full > 0) {
      const left = Math.max(0, r.left - (this.time - r.leftAt) * 1000 * this.feeds.mapSecPerWallSec());
      share = 1 - left / r.full;
    }
    const quiet = act === NpcAct.NPC_ACT_NONE || act === NpcAct.NPC_ACT_IDLE || act === NpcAct.NPC_ACT_MOVE;
    if (act === NpcAct.NPC_ACT_NONE) act = r.speed > MONSTER_LOOK.movePxPerSec ? ACT.move : ACT.idle;
    let struck = false;
    if (quiet && this.time - r.strikeAt < MONSTER_LOOK.strikeSec) {
      act = STRIKE_ACT[r.kind];
      phase = this.time - r.strikeAt;
      share = -1;
      struck = true;
    }
    const s = this.st;
    actPose(r.kind, act, phase, share, s);
    s.t = this.time + r.offset;
    s.alpha = 1;
    s.bodies = r.kind === 4 ? r.hullPct / 100 : 1;
    if (aims(act)) this.aim(r, p, k, struck, s);
    if (act === ACT.pulse) this.arcs(p, k, s);
    MONSTER_BODIES[r.kind][r.design](pen, s);
  }

  /** The target of a body in its local units: the victim of a gun hit, else
   *  the nearest hull of a faction in reach. */
  private aim(r: Rec, p: SignaturePose, k: number, struck: boolean, s: MonsterState): void {
    let gx: number, gy: number;
    if (struck) { gx = r.strikeX; gy = r.strikeY; } else {
      const f = this.feeds.foe(p.x, p.y, AIM_UNITS * k);
      if (!f) return;
      gx = f.x; gy = f.y;
    }
    const dx = (gx - p.x) / k, dy = (gy - p.y) / k;
    const ca = Math.cos(-p.head), sa = Math.sin(-p.head);
    s.tx = dx * ca - dy * sa;
    s.ty = dx * sa + dy * ca;
    s.hasTarget = true;
  }

  /** The arcs of a discharge: every hull of a faction in reach, in the
   *  body's local units (R2719). The pairs land in the state's own array. */
  private arcs(p: SignaturePose, k: number, s: MonsterState): void {
    const foes = this.feeds.foes;
    if (!foes) return;
    const z = s.zaps;
    const n = foes(p.x, p.y, AIM_UNITS * k, z);
    const ca = Math.cos(-p.head), sa = Math.sin(-p.head);
    for (let i = 0; i < n; i++) {
      const dx = (z[2 * i] - p.x) / k, dy = (z[2 * i + 1] - p.y) / k;
      z[2 * i] = dx * ca - dy * sa;
      z[2 * i + 1] = dx * sa + dy * ca;
    }
    s.nZaps = n;
  }

  private packBursts(frame: FrameInfo, dt: number): void {
    const s = this.st;
    const c = frame.cull;
    for (let i = 0; i < this.nBursts; i++) {
      const b = this.bursts[i];
      b.age += dt;
      if (b.age >= POD_BURST_SEC) {
        // Swap the spent burst out with the last live one.
        const last = this.bursts[this.nBursts - 1];
        this.bursts[this.nBursts - 1] = b;
        this.bursts[i] = last;
        this.nBursts--;
        i--;
        continue;
      }
      if (outside(b.x, b.y, c.x0 - 20, c.y0 - 20, c.x1 + 20, c.y1 + 20)) continue;
      this.pen.place(b.x, b.y, 0, 1);
      this.pen.alpha = 1;
      s.t = this.time;
      s.alpha = 1;
      drawPodBurst(b.design, this.pen, s, b.age / POD_BURST_SEC);
    }
  }

  draw(ctx: DrawCtx): void {
    this.quads.flush(ctx.cam);
    this.segs.flush(ctx.cam);
    this.circles.flush(ctx.cam);
  }
}
